import { z } from "zod";
import type { LichessProfile, LichessGameSource } from "./lichess";
export const Color = z.enum(["w", "b"]);
export type Color = z.infer<typeof Color>;
export const CoachMode = z.enum([
  "guided",
  "active",
  "quiet",
  "postgame",
  "socratic",
  "direct",
]);
export type CoachMode = z.infer<typeof CoachMode>;
export const Workspace = z.enum([
  "board",
  "library",
  "practice",
  "plan",
  "memory",
  "lesson",
  "settings",
  "compare",
]);
export type Workspace = z.infer<typeof Workspace>;
export type Difficulty = "gentle" | "balanced" | "challenging";
export const LiveReview = z.enum(["off", "mistakes", "every-move"]);
export type LiveReview = z.infer<typeof LiveReview>;
export interface Game {
  source?: z.infer<typeof LichessGameSource>;
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  initialFen: string;
  moves: string[];
  revision: number;
  mode: "analysis" | "play" | "import";
  humanColor: Color;
  orientation: Color;
  difficulty: Difficulty;
  coaching: CoachMode;
  hintFirst: boolean;
  liveReview?: LiveReview;
  liveReviewFrom?: number;
  result: string;
  endReason?: string;
  pinned: boolean;
  headers: Record<string, string>;
  clock: {
    initial: number;
    increment: number;
    w: number;
    b: number;
    startedAt: number | null;
  };
}
export interface Variation {
  id: string;
  gameId: string;
  sourcePly: number;
  title: string;
  initialFen: string;
  moves: string[];
  revision: number;
  pinned: boolean;
  liveReviewFrom?: number;
}
export interface AnalysisLine {
  moves: string[];
  san: string[];
  cp: number | null;
  mate: number | null;
  depth: number;
}
export interface Analysis {
  fen: string;
  gameId: string;
  revision: number;
  variationId: string | null;
  lines: AnalysisLine[];
  engine: string;
  at: number;
}
export type Pattern =
  "missed-mate" | "allowed-mate" | "missed-capture" | "lost-advantage";
export interface Evidence {
  id: string;
  gameId: string;
  ply: number;
  fen: string;
  played: string;
  best: string;
  loss: number;
  pattern: Pattern;
  before: AnalysisLine;
  after: AnalysisLine;
  at: number;
  dismissed: boolean;
  note: string;
}
export interface Exercise {
  id: string;
  title: string;
  fen: string;
  solution: string[];
  san: string[];
  evidenceId?: string;
  gameId?: string;
  source: "personal" | "curriculum";
  variant?: "original" | "perspective";
  concept: string;
  depth: number;
  attempts: {
    at: number;
    outcome: "independent" | "hinted" | "revealed" | "incorrect";
    move: string;
  }[];
  dueAt: number;
}
export interface PracticeSession {
  exerciseIds: string[];
  index: number;
  hintLevel: number;
  solved: boolean;
  feedback: string;
  attempts: number;
}
export interface TrainingPlan {
  id: string;
  title: string;
  minutes: number;
  items: {
    title: string;
    minutes: number;
    exerciseIds: string[];
    done: boolean;
  }[];
}
export interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  at: number;
  reference?: { gameId: string; ply: number; variationId?: string };
}
export interface AppState {
  lichess?: LichessProfile;
  version: 1;
  revision: number;
  workspace: Workspace | null;
  games: Game[];
  activeGameId: string | null;
  variations: Variation[];
  activeVariationId: string | null;
  viewPly: number | null;
  messages: Message[];
  evidence: Evidence[];
  exercises: Exercise[];
  practice: PracticeSession | null;
  plans: TrainingPlan[];
  activePlanId: string | null;
  lesson: "opposition" | "development" | "calculation" | null;
  preferences: {
    coaching: CoachMode;
    hintFirst: boolean;
    length: "brief" | "standard" | "deep";
    side: Color;
    speech: boolean;
    handsFree: boolean;
    arrows: boolean;
    memory: boolean;
    liveReview?: LiveReview;
  };
  libraryFilter: {
    query: string;
    result: string;
    color: string;
    after: string;
    before: string;
    opening: string;
    opponent: string;
    timeControl: string;
  };
  studio?: {
    kind:
      | "opening"
      | "calculation"
      | "teachback"
      | "conversion"
      | "defense"
      | "preparation";
    title: string;
    description: string;
    question: string;
    gameId: string;
    attempts: { line: string[]; feedback: string; at: number }[];
    referenceGameIds: string[];
  } | null;
  analysis: Analysis | null;
  comparison: { move: string; line: AnalysisLine }[];
  moveReviews?: MoveReview[];
  annotations?: {
    fen: string;
    arrows: { from: string; to: string; label?: string }[];
    squares: string[];
  } | null;
  teaching?: {
    gameId: string;
    title: string;
    text: string;
    question: string;
  } | null;
}
export interface MoveReview {
  id: string;
  gameId: string;
  variationId: string | null;
  ply: number;
  historyKey: string;
  beforeFen: string;
  afterFen: string;
  played: string;
  san: string;
  color: Color;
  loss: number;
  before: AnalysisLine;
  after: AnalysisLine;
  text: string;
  explanation: "coach" | "engine";
  at: number;
}
export interface ProviderStatus {
  configured: boolean;
  provider: string;
  model: string;
  transcription: boolean;
}
export const emptyState = (): AppState => ({
  version: 1,
  revision: 0,
  workspace: null,
  games: [],
  activeGameId: null,
  variations: [],
  activeVariationId: null,
  viewPly: null,
  messages: [],
  moveReviews: [],
  evidence: [],
  exercises: [],
  practice: null,
  plans: [],
  activePlanId: null,
  lesson: null,
  preferences: {
    coaching: "guided",
    liveReview: "mistakes",
    hintFirst: true,
    length: "standard",
    side: "w",
    speech: false,
    handsFree: false,
    arrows: true,
    memory: true,
  },
  libraryFilter: {
    query: "",
    result: "",
    color: "",
    after: "",
    before: "",
    opening: "",
    opponent: "",
    timeControl: "",
  },
  analysis: null,
  comparison: [],
});
export const patternLabels: Record<Pattern, string> = {
  "missed-mate": "Recognizing a mating opportunity",
  "allowed-mate": "Checking the opponent’s forcing replies",
  "missed-capture": "Evaluating a forcing capture",
  "lost-advantage": "Comparing candidate moves",
};
export const coachLabels: Record<CoachMode, string> = {
  guided: "Guided learning",
  active: "Active feedback",
  quiet: "Quiet observation",
  postgame: "Post-game review",
  socratic: "Socratic questions",
  direct: "Direct explanation",
};
